<template>
  <div class="card border-0 rounded-3 mb-4 bg-white shadow-sm">
    <div class="card-header bg-light-subtle d-flex justify-content-between align-items-center py-3 px-4">
      <div class="d-flex align-items-center gap-2">
        <span class="material-symbols-outlined text-primary fs-22">spellcheck</span>
        <h5 class="mb-0 fs-15 fw-bold text-dark d-flex align-items-center gap-2">
          <span>{{ order?.service_type === 'AI_CHECK' ? 'Protokol Penyuntingan Tata Bahasa (SOP RBN/SOP-RC/GRM/001)' : 'Protokol Penyuntingan & Parafrase (SOP RBN/SOP-RC/GRM/001)' }}</span>
          <span v-if="order?.service_type === 'AI_CHECK'" class="badge bg-primary-subtle text-primary border border-primary">
            Bagian Layanan Drillbit
          </span>
        </h5>
      </div>
      <span v-if="protocol" class="badge bg-success-subtle text-success fs-12 px-3 py-2">
        {{ protocol.protocol_number }}
      </span>
      <span v-else class="badge bg-warning-subtle text-warning fs-12 px-3 py-2">
        Belum Dibuat
      </span>
    </div>

    <div class="card-body p-4">
      <!-- Existing Protocol Overview -->
      <div v-if="protocol" class="mb-4 p-3 rounded-3 bg-light border border-light-subtle">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div class="text-muted fs-12">Nomor Protokol Resmi</div>
            <h5 class="fw-bold text-primary mb-0">{{ protocol.protocol_number }}</h5>
          </div>
          <div class="d-flex gap-2">
            <button @click="generatePdf" :disabled="generatingPdf" class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1">
              <span v-if="generatingPdf" class="spinner-border spinner-border-sm"></span>
              <span class="material-symbols-outlined fs-16">picture_as_pdf</span>
              <span>{{ generatingPdf ? 'Membuat PDF...' : 'Regenerate PDF Protokol' }}</span>
            </button>
            <a v-if="order.generated_protocol_pdf" :href="getFileUrl(order.generated_protocol_pdf)" target="_blank" class="btn btn-sm btn-success d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">download</span>
              <span>Unduh Dokumen Resmi A4</span>
            </a>
            <a v-if="protocol.verification?.verification_code" :href="getVerificationUrl(protocol.verification.verification_code)" target="_blank" class="btn btn-sm btn-info text-white d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">verified</span>
              <span>Cek Verifikasi Publik</span>
            </a>
          </div>
        </div>

        <div class="row g-2 fs-13">
          <div class="col-md-3">
            <span class="text-muted">Skor Kinerja:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.score_before || '-' }} &rarr; {{ protocol.score_after || '-' }}/100</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Level Parafrase:</span>
            <span class="fw-semibold text-dark ms-1">{{ protocol.paraphrase_level }}</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Keputusan:</span>
            <span class="badge ms-1 bg-success">{{ protocol.status_decision }}</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Editor:</span>
            <span class="fw-semibold text-dark ms-1">{{ protocol.editor_name }}</span>
          </div>
        </div>
      </div>

      <form @submit.prevent="saveProtocol">
        <!-- Target Journal & Paraphrase Level -->
        <div class="row mb-3">
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Jurnal Sasaran</label>
            <input type="text" class="form-control form-control-sm" v-model="form.target_journal" placeholder="Jurnal Nasional Sinta 2 / Scopus Q2" />
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Bahasa Naskah <span class="text-danger">*</span></label>
            <input type="text" class="form-control form-control-sm" v-model="form.language" required placeholder="Bahasa Indonesia / English" />
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Level Parafrase <span class="text-danger">*</span></label>
            <select class="form-select form-select-sm" v-model="form.paraphrase_level" required>
              <option value="Level 1 - Penyuntingan ringan">Level 1 - Penyuntingan ringan</option>
              <option value="Level 2 - Parafrase kalimat">Level 2 - Parafrase kalimat</option>
              <option value="Level 3 - Rekonstruksi akademik">Level 3 - Rekonstruksi akademik</option>
              <option value="Level 4 - QC substantif bahasa">Level 4 - QC substantif bahasa</option>
            </select>
          </div>
        </div>

        <!-- Matriks 4 Aspek -->
        <div class="p-3 bg-light rounded-2 mb-3 border">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="fw-bold fs-13 text-dark">Matriks 4 Aspek Grammarly Pro &amp; Editorial Manusia</div>
            <span class="badge bg-primary-subtle text-primary fs-11 d-inline-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-13">auto_awesome</span>
              <span>Total Skor Terisi Otomatis (Rata-rata 4 Aspek)</span>
            </span>
          </div>
          
          <div class="table-responsive mb-3">
            <table class="table table-sm table-bordered align-middle fs-12 mb-0 bg-white">
              <thead class="table-light">
                <tr>
                  <th style="width: 30%;">Aspek</th>
                  <th style="width: 20%; text-align: center;">Skor Sebelum</th>
                  <th style="width: 20%; text-align: center;">Skor Sesudah</th>
                  <th style="width: 30%;">Status Aspek</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>1. Correctness (Tata Bahasa)</strong></td>
                  <td><input type="number" class="form-control form-control-sm text-center" v-model.number="form.correctness_before" min="0" max="100" /></td>
                  <td><input type="number" class="form-control form-control-sm text-center" v-model.number="form.correctness_after" min="0" max="100" /></td>
                  <td>
                    <select class="form-select form-select-sm" v-model="form.correctness_status">
                      <option value="Selesai">Selesai</option>
                      <option value="Review">Review</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><strong>2. Clarity (Kejelasan Kalimat)</strong></td>
                  <td><input type="number" class="form-control form-control-sm text-center" v-model.number="form.clarity_before" min="0" max="100" /></td>
                  <td><input type="number" class="form-control form-control-sm text-center" v-model.number="form.clarity_after" min="0" max="100" /></td>
                  <td>
                    <select class="form-select form-select-sm" v-model="form.clarity_status">
                      <option value="Selesai">Selesai</option>
                      <option value="Review">Review</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><strong>3. Engagement (Kosakata Ilmiah)</strong></td>
                  <td><input type="number" class="form-control form-control-sm text-center" v-model.number="form.engagement_before" min="0" max="100" /></td>
                  <td><input type="number" class="form-control form-control-sm text-center" v-model.number="form.engagement_after" min="0" max="100" /></td>
                  <td>
                    <select class="form-select form-select-sm" v-model="form.engagement_status">
                      <option value="Selesai">Selesai</option>
                      <option value="Review">Review</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><strong>4. Delivery (Nada Akademik)</strong></td>
                  <td><input type="number" class="form-control form-control-sm text-center" v-model.number="form.delivery_before" min="0" max="100" /></td>
                  <td><input type="number" class="form-control form-control-sm text-center" v-model.number="form.delivery_after" min="0" max="100" /></td>
                  <td>
                    <select class="form-select form-select-sm" v-model="form.delivery_status">
                      <option value="Selesai">Selesai</option>
                      <option value="Review">Review</option>
                    </select>
                  </td>
                </tr>
                <tr class="table-light">
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <strong>Total Grammarly Score (/100)</strong>
                      <span class="badge bg-primary-subtle text-primary fs-11">Auto</span>
                    </div>
                    <span class="text-muted fs-11">Rata-rata 4 aspek di atas</span>
                  </td>
                  <td><input type="number" class="form-control form-control-sm text-center fw-semibold" v-model.number="form.score_before" min="0" max="100" title="Dihitung otomatis dari rata-rata 4 aspek (dapat disesuaikan)" /></td>
                  <td><input type="number" class="form-control form-control-sm text-center fw-bold text-success" v-model.number="form.score_after" min="0" max="100" title="Dihitung otomatis dari rata-rata 4 aspek (dapat disesuaikan)" /></td>
                  <td class="text-center text-success fw-bold">Skor Keseluruhan</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Checklist Preservasi Substansi -->
        <div class="p-3 bg-light rounded-2 mb-3 border">
          <div class="fw-bold fs-13 mb-2 text-dark">Checklist Preservasi Substansi (6 Parameter Integritas)</div>
          <div class="row g-2 fs-12">
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="form.preserve_numbers" id="pNum">
                <label class="form-check-label" for="pNum">Preservasi Angka & Data</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="form.preserve_citations" id="pCit">
                <label class="form-check-label" for="pCit">Preservasi Sitasi & Referensi</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="form.preserve_technical_terms" id="pTerm">
                <label class="form-check-label" for="pTerm">Preservasi Istilah Teknis</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="form.preserve_methods" id="pMeth">
                <label class="form-check-label" for="pMeth">Preservasi Metodologi</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="form.preserve_results" id="pRes">
                <label class="form-check-label" for="pRes">Preservasi Temuan Utama</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="form.preserve_meaning" id="pMean">
                <label class="form-check-label" for="pMean">Preservasi Makna/Argumen</label>
              </div>
            </div>
          </div>
        </div>

        <!-- Rejected Suggestions Log -->
        <div class="p-3 bg-light-subtle rounded-2 mb-3 border">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="fw-bold fs-13 text-dark">Log Saran Grammarly yang Ditolak (Human Expert Override)</div>
            <button type="button" @click="addRejectedLog" class="btn btn-xs btn-outline-primary btn-sm fs-11">
              + Tambah Log Penolakan
            </button>
          </div>
          
          <div v-for="(log, idx) in form.rejected_logs" :key="idx" class="row g-2 mb-2 p-2 bg-white rounded border align-items-center">
            <div class="col-md-3">
              <label class="form-label fs-11 text-muted mb-1 d-block">Saran Grammarly</label>
              <input type="text" class="form-control form-control-sm fs-12" v-model="log.original_suggestion" @input="log.suggestion_excerpt = log.original_suggestion" placeholder="Saran otomatis..." />
            </div>
            <div class="col-md-2">
              <label class="form-label fs-11 text-muted mb-1 d-block">Aspek</label>
              <select class="form-select form-select-sm fs-12" v-model="log.aspect">
                <option value="Correctness">Correctness</option>
                <option value="Clarity">Clarity</option>
                <option value="Engagement">Engagement</option>
                <option value="Delivery">Delivery</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label fs-11 text-muted mb-1 d-block">Alasan Penolakan</label>
              <select class="form-select form-select-sm fs-12" v-model="log.reason_code" @change="log.rejection_code = log.reason_code">
                <option value="TECH_TERM">TECH_TERM (Istilah Teknis)</option>
                <option value="MEANING_RISK">MEANING_RISK (Risiko Makna)</option>
                <option value="CITATION_STYLE">CITATION_STYLE (Sitasi)</option>
                <option value="DIRECT_QUOTE">DIRECT_QUOTE (Kutipan)</option>
                <option value="JOURNAL_STYLE">JOURNAL_STYLE (Gaya Jurnal)</option>
                <option value="FALSE_POSITIVE">FALSE_POSITIVE (Deteksi Keliru)</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label fs-11 text-muted mb-1 d-block">Justifikasi Editor</label>
              <input type="text" class="form-control form-control-sm fs-12" v-model="log.editor_justification" @input="log.rejection_reason = log.editor_justification" placeholder="Justifikasi penolakan..." />
            </div>
            <div class="col-md-1 text-center pt-3">
              <button type="button" @click="removeRejectedLog(idx)" class="btn btn-link text-danger p-0" title="Hapus baris">
                <span class="material-symbols-outlined fs-18">delete</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Verification Metadata & Signatures -->
        <div class="row mb-3">
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Nama Penulis (Masked)</label>
            <input type="text" class="form-control form-control-sm" v-model="form.author_name_masked" required />
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Judul Dokumen (Masked)</label>
            <input type="text" class="form-control form-control-sm" v-model="form.document_title_masked" required />
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Status Keputusan QC <span class="text-danger">*</span></label>
            <select class="form-select form-select-sm" v-model="form.status_decision" required>
              <option value="PASS / Memenuhi">PASS / Memenuhi</option>
              <option value="REVISE / Perlu Revisi">REVISE / Perlu Revisi</option>
              <option value="CLARIFY / Perlu Klarifikasi">CLARIFY / Perlu Klarifikasi</option>
              <option value="ESCALATE / Eskalasi QC">ESCALATE / Eskalasi QC</option>
            </select>
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label fs-13 fw-semibold">SHA-256 Hash Naskah</label>
            <input type="text" class="form-control form-control-sm font-monospace" v-model="form.file_hash_sha256" required />
          </div>
          <div class="col-12 mt-2">
            <div class="card bg-light-subtle border p-3 rounded-3 mb-2">
              <h6 class="fs-13 fw-bold text-dark mb-2 d-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-18 text-primary">draw</span>
                <span>Editor & Penandatangan Pengesahan Protokol</span>
              </h6>
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Nama Editor Bahasa <span class="text-danger">*</span></label>
                  <input type="text" class="form-control form-control-sm" v-model="form.editor_name" required />
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Pilih Penandatangan Terdaftar</label>
                  <select class="form-select form-select-sm" v-model="form.signatory_id" @change="onSignatoryChange">
                    <option :value="null">-- Gunakan Penandatangan Default --</option>
                    <option v-for="sig in signatories" :key="sig.id" :value="sig.id">
                      {{ sig.name }} ({{ sig.position }}) {{ sig.is_default ? '★ Default' : '' }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Nama & Jabatan Pengesahan</label>
                  <input type="text" class="form-control form-control-sm mb-1" v-model="form.signatory_name" placeholder="Nama penandatangan" />
                  <input type="text" class="form-control form-control-sm" v-model="form.signatory_position" placeholder="Jabatan penandatangan" />
                </div>
                <div v-if="selectedSignatoryPreviewUrl" class="col-12 mt-1">
                  <div class="d-flex align-items-center gap-3 p-2 bg-white rounded border">
                    <span class="fs-11 text-muted fw-semibold">Preview Tanda Tangan:</span>
                    <img :src="selectedSignatoryPreviewUrl" alt="Tanda Tangan" style="height: 32px; max-width: 120px; object-fit: contain;" />
                    <span class="fs-11 text-success d-flex align-items-center gap-1">
                      <span class="material-symbols-outlined fs-14">check_circle</span>
                      <span>Siap disematkan pada sertifikat protokol</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4">
          <button type="submit" class="btn btn-primary px-4 py-2 d-flex align-items-center gap-2" :disabled="submitting">
            <span v-if="submitting" class="spinner-border spinner-border-sm"></span>
            <span class="material-symbols-outlined fs-18">save</span>
            <span>{{ protocol ? 'Perbarui Protokol' : 'Simpan & Terbitkan Protokol RC-GRM' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted, computed, watch } from 'vue';
import api from '../../../services/api';
import protocolSignatoryService from '../../../services/protocolSignatoryService';

export default defineComponent({
  name: 'GrammarlyProtocolForm',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const protocol = computed(() => props.order?.protocol_grammarly || null);
    const signatories = ref<any[]>([]);
    const submitting = ref(false);
    const generatingPdf = ref(false);

    const form = reactive({
      target_journal: 'Jurnal Terakreditasi Nasional / Internasional',
      language: props.order?.paraphrase_detail?.language === 'id' ? 'Bahasa Indonesia' : 'English',
      paraphrase_level: 'Level 2 - Parafrase kalimat',
      file_hash_sha256: Array.from({length: 64}, () => Math.floor(Math.random()*16).toString(16)).join(''),
      score_before: 72,
      score_after: 95,
      correctness_before: 68,
      correctness_after: 95,
      correctness_status: 'Selesai',
      clarity_before: 70,
      clarity_after: 94,
      clarity_status: 'Selesai',
      engagement_before: 75,
      engagement_after: 92,
      engagement_status: 'Selesai',
      delivery_before: 74,
      delivery_after: 98,
      delivery_status: 'Selesai',
      preserve_numbers: true,
      preserve_citations: true,
      preserve_technical_terms: true,
      preserve_methods: true,
      preserve_results: true,
      preserve_meaning: true,
      preservation_notes: 'Seluruh sitasi, angka formula, dan makna asli dipertahankan secara ketat.',
      status_decision: 'PASS / Memenuhi',
      editor_name: 'Editor Bahasa RBN',
      qc_name: 'Tim QC RBN',
      service_manager_name: 'Manajer Layanan RBN',
      director_name: 'Direktur RBN',
      signatory_id: null as number | null,
      signatory_name: '',
      signatory_position: '',
      signatory_image: null as string | null,
      author_name_masked: maskName(props.order?.customer?.name || 'Customer RBN'),
      document_title_masked: 'Naskah ' + (props.order?.order_code || ''),
      institution: 'Institusi Terdaftar',
      rejected_logs: [
        {
          original_suggestion: 'Change "in situ" to "in place"',
          suggestion_excerpt: 'Change "in situ" to "in place"',
          aspect: 'Correctness',
          reason_code: 'TECH_TERM',
          rejection_code: 'TECH_TERM',
          editor_justification: 'Istilah teknis biologi dipertahankan sesuai konvensi internasional.',
          rejection_reason: 'Istilah teknis biologi dipertahankan sesuai konvensi internasional.'
        }
      ]
    });

    // Auto-calculate Total Grammarly Score (Skor Sebelum) from the 4 aspects
    watch(
      () => [
        form.correctness_before,
        form.clarity_before,
        form.engagement_before,
        form.delivery_before
      ],
      ([cb, clb, eb, db]) => {
        const vals = [cb, clb, eb, db]
          .map(v => Number(v))
          .filter(v => !isNaN(v) && v !== null && v !== undefined && v > 0);
        if (vals.length > 0) {
          form.score_before = Math.round(vals.reduce((sum, v) => sum + v, 0) / vals.length);
        }
      }
    );

    // Auto-calculate Total Grammarly Score (Skor Sesudah) from the 4 aspects
    watch(
      () => [
        form.correctness_after,
        form.clarity_after,
        form.engagement_after,
        form.delivery_after
      ],
      ([ca, cla, ea, da]) => {
        const vals = [ca, cla, ea, da]
          .map(v => Number(v))
          .filter(v => !isNaN(v) && v !== null && v !== undefined && v > 0);
        if (vals.length > 0) {
          form.score_after = Math.round(vals.reduce((sum, v) => sum + v, 0) / vals.length);
        }
      }
    );

    function maskName(str: string): string {
      if (!str || str.length <= 2) return str;
      const parts = str.split(' ');
      return parts.map(p => p[0] + '***' + (p.length > 3 ? p[p.length - 1] : '')).join(' ');
    }

    const addRejectedLog = () => {
      form.rejected_logs.push({
        original_suggestion: '',
        suggestion_excerpt: '',
        aspect: 'Correctness',
        reason_code: 'TECH_TERM',
        rejection_code: 'TECH_TERM',
        editor_justification: '',
        rejection_reason: ''
      });
    };

    const removeRejectedLog = (idx: number) => {
      form.rejected_logs.splice(idx, 1);
    };

    const saveProtocol = async () => {
      submitting.value = true;
      try {
        if (protocol.value) {
          await api.put(`/protocol-grammarly/${protocol.value.id}`, form);
          alert('Protokol berhasil diperbarui.');
        } else {
          await api.post(`/orders/${props.order.order_id}/protocol-grammarly`, form);
          alert('Protokol RC-GRM berhasil diterbitkan!');
        }
        emit('refresh');
      } catch (err: any) {
        console.error('Error saving grammarly protocol:', err);
        alert(err.response?.data?.message || 'Gagal menyimpan protokol.');
      } finally {
        submitting.value = false;
      }
    };

    const generatePdf = async () => {
      generatingPdf.value = true;
      try {
        await api.post(`/orders/${props.order.order_id}/generate-protocol-pdf`);
        alert('PDF Protokol resmi berhasil digenerate.');
        emit('refresh');
      } catch (err: any) {
        console.error('Error generating PDF:', err);
        alert(err.response?.data?.message || 'Gagal generate PDF.');
      } finally {
        generatingPdf.value = false;
      }
    };

    const getFileUrl = (path?: string | null) => {
      if (!path) return '';
      if (path.startsWith('blob:') || path.startsWith('data:')) return path;
      if (path.includes('/storage/')) {
        const cleanPath = path.replace(/^https?:\/\/[^/]+\/storage\//, '').replace(/^storage\//, '').replace(/^\//, '');
        const baseUrl = process.env.NODE_ENV === 'production'
          ? 'https://backend-beta.rayacheck.id'
          : 'http://jurnal-backend.test';
        return `${baseUrl}/storage/${cleanPath}`;
      }
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      const cleanPath = path.replace(/^\//, '');
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${cleanPath}`;
    };

    const getVerificationUrl = (code: string) => {
      const baseUrl = window.location.origin;
      return `${baseUrl}/verify/${code}`;
    };

    const loadSignatories = async () => {
      try {
        const res = await protocolSignatoryService.getActive();
        signatories.value = res.data.data || [];
        if (!form.signatory_id && !protocol.value?.signatory_id) {
          const def = signatories.value.find((s: any) => s.is_default) || signatories.value[0];
          if (def) {
            form.signatory_id = def.id;
            form.signatory_name = def.name;
            form.signatory_position = def.position;
            form.signatory_image = def.signature_image;
            form.director_name = def.name;
          }
        }
      } catch (err) {
        console.error('Failed to load active signatories:', err);
      }
    };

    const onSignatoryChange = () => {
      const chosen = signatories.value.find((s: any) => s.id === form.signatory_id);
      if (chosen) {
        form.signatory_name = chosen.name;
        form.signatory_position = chosen.position;
        form.signatory_image = chosen.signature_image;
        form.director_name = chosen.name;
      }
    };

    const selectedSignatoryPreviewUrl = computed(() => {
      let raw = '';
      if (form.signatory_id) {
        const chosen = signatories.value.find((s: any) => s.id === form.signatory_id);
        if (chosen) raw = chosen.signature_image_url || chosen.signature_image || '';
      } else if (protocol.value?.signatory_image_url || protocol.value?.signatory_image) {
        raw = protocol.value.signatory_image_url || protocol.value.signatory_image || '';
      } else {
        const def = signatories.value.find((s: any) => s.is_default);
        raw = def?.signature_image_url || def?.signature_image || '';
      }
      return raw ? getFileUrl(raw) : null;
    });

    onMounted(async () => {
      await loadSignatories();
      if (protocol.value) {
        Object.assign(form, protocol.value);
        const existingLogs = protocol.value.rejected_logs || protocol.value.rejectedLogs;
        if (existingLogs && Array.isArray(existingLogs)) {
          form.rejected_logs = existingLogs.map((log: any) => ({
            original_suggestion: log.suggestion_excerpt || log.original_suggestion || '',
            suggestion_excerpt: log.suggestion_excerpt || log.original_suggestion || '',
            aspect: log.aspect || 'Correctness',
            reason_code: log.reason_code || log.rejection_code || 'TECH_TERM',
            rejection_code: log.reason_code || log.rejection_code || 'TECH_TERM',
            editor_justification: log.rejection_reason || log.editor_justification || '',
            rejection_reason: log.rejection_reason || log.editor_justification || ''
          }));
        }
      }
    });

    return {
      protocol,
      signatories,
      onSignatoryChange,
      selectedSignatoryPreviewUrl,
      form,
      submitting,
      generatingPdf,
      addRejectedLog,
      removeRejectedLog,
      saveProtocol,
      generatePdf,
      getFileUrl,
      getVerificationUrl
    };
  }
});
</script>
